import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useRateCardsActions } from "tap/store/rateCards/useRateCards.js";
import { tapApi } from "tap/api.js";
import { filterCriteria } from "tap/utils/filterCriteria.js";

// Minimum time to show the loading spinner (in ms)
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates rate cards data fetching logic.
 * Handles loading states, error toasts, and Redux updates.
 *
 * @returns {Object} Object containing fetchRateCards function and rate cards state values
 */
export function useRateCardsData() {
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updateRateCards } = useRateCardsActions();
  const { filterForm, rateCardsRange, rateCardsSort, itemsPerPage } =
    useSelector((s) => s.rateCards);

  /**
   * Fetches rate cards from the API with the given range.
   * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
   * @param {string} range - The pagination range (e.g., "0,25")
   * @param {Object|null} customFilterData - Optional filter data to use instead of Redux filterForm
   * @param {Array|null} customSort - Optional sort array to use instead of Redux rateCardsSort
   * @returns {Promise<Object|null>} The API response data or null on error
   */
  const fetchRateCards = useCallback(
    async (range, customFilterData = null, customSort = null) => {
      const startTime = Date.now();
      setLoadingTrue();

      const filterData = customFilterData ?? filterForm;
      const sortData = customSort ?? rateCardsSort;

      try {
        const data = await tapApi.rateCards(
          filterCriteria(filterData, "", "rateCards"),
          range,
          sortData,
        );

        if (data?.code || data?.httpCode || data?.error) {
          showErrorToast(data?.message || "Failed to fetch rate cards data.");
          return null;
        }

        return data;
      } catch (error) {
        showErrorToast(error?.message || "Failed to fetch rate cards data.");
        return null;
      } finally {
        // Ensure minimum loading duration for consistent UX
        const elapsed = Date.now() - startTime;
        const remainingTime = MIN_LOADING_DURATION - elapsed;

        if (remainingTime > 0) {
          setTimeout(() => setLoadingFalse(), remainingTime);
        } else {
          setLoadingFalse();
        }
      }
    },
    [
      filterForm,
      rateCardsSort,
      setLoadingTrue,
      setLoadingFalse,
      showErrorToast,
    ],
  );

  return {
    fetchRateCards,
    updateRateCards,
    itemsPerPage,
    rateCardsRange,
    filterForm,
    rateCardsSort,
  };
}
